import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import {
  createPlannerGradeSubject,
  createPlannerSubject,
  deletePlannerGradeSubject,
  deletePlannerSubject,
  fetchPlannerGradeGroup,
  fetchPlannerGradeSubject,
  fetchPlannerSubject,
  updatePlannerSubject
} from 'api'
import { showToast } from 'app/helpers'

const items = (d) =>
  d && Array.isArray(d.results) ? d.results : Array.isArray(d) ? d : []

// Colour tokens shared with the planner prototype (main = chip background).
const PALETTE = {
  green: '#2d6a4f',
  blue: '#1d3557',
  red: '#9b2226',
  amber: '#7b4f12',
  purple: '#5b2c87',
  teal: '#0f5c5c',
  pink: '#a8326a',
  slate: '#3d4a5c'
}
const COLOR_KEYS = Object.keys(PALETTE)
const chipColor = (c) => PALETTE[(c || '').toLowerCase()] || PALETTE.slate

const blankForm = () => ({ uuid: null, label: '', icon: '🌟', color: 'slate' })

/** Activity subjects admin: master list + which grade groups use each one. */
const Subjects = () => {
  const [grades, setGrades] = useState([])
  const [gradeIdx, setGradeIdx] = useState(0)
  const [subjects, setSubjects] = useState([]) // all subjects (master list)
  const [linkBySubject, setLinkBySubject] = useState({}) // subjectUuid -> link uuid
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState(null) // null = dialog closed

  const grade = grades[gradeIdx]

  const loadSubjects = () =>
    fetchPlannerSubject({})
      .then((s) => setSubjects(items(s)))
      .catch(() => showToast('Failed to load subjects', 'danger'))

  const loadLinks = (gradeUuid) => {
    if (!gradeUuid) return Promise.resolve()
    return fetchPlannerGradeSubject({ queries: { grade_group: gradeUuid } })
      .then((gs) => {
        const map = {}
        items(gs).forEach((l) => { map[l.subject] = l.uuid })
        setLinkBySubject(map)
      })
      .catch(() => showToast('Failed to load grade subjects', 'danger'))
  }

  useEffect(() => {
    fetchPlannerGradeGroup({})
      .then((g) => setGrades(items(g)))
      .catch(() => showToast('Failed to load grades', 'danger'))
    loadSubjects()
  }, [])

  useEffect(() => {
    setLoading(true)
    loadLinks(grade && grade.uuid).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade && grade.uuid])

  const toggleUsed = async (subjectUuid, checked) => {
    if (!grade) return
    try {
      if (checked) {
        await createPlannerGradeSubject({ grade_group: grade.uuid, subject: subjectUuid })
      } else {
        const linkUuid = linkBySubject[subjectUuid]
        if (linkUuid) await deletePlannerGradeSubject({ ids: [linkUuid] })
      }
      await loadLinks(grade.uuid)
    } catch (e) {
      showToast('Failed to update — please retry', 'danger')
    }
  }

  const saveSubject = async () => {
    if (!form.label.trim()) {
      showToast('Subject name is required', 'danger')
      return
    }
    const payload = {
      uuid: form.uuid,
      label: form.label.trim(),
      icon: form.icon.trim() || '🌟',
      color: form.color
    }
    try {
      if (form.uuid) await updatePlannerSubject(payload)
      else await createPlannerSubject(payload)
      setForm(null)
      await loadSubjects()
      showToast('Subject saved', 'success')
    } catch (e) {
      showToast('Failed to save subject', 'danger')
    }
  }

  const deleteSubject = async (s) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete subject "${s.label}"? It will be removed from every grade group.`)) return
    try {
      await deletePlannerSubject({ ids: [s.uuid] })
      await loadSubjects()
      await loadLinks(grade && grade.uuid)
      showToast('Subject deleted', 'success')
    } catch (e) {
      showToast('Failed to delete subject', 'danger')
    }
  }

  const gradeName = grade ? grade.name : ''

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>Activity Subjects</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Create or delete activity types (Language, Numeracy, …) and choose which
        grade groups use each one.
      </Typography>

      <Tabs value={gradeIdx} onChange={(e, v) => setGradeIdx(v)} indicatorColor="primary"
        textColor="primary" variant="scrollable">
        {grades.map((g) => <Tab key={g.uuid} label={`${g.emoji || ''} ${g.name}`} />)}
      </Tabs>

      <Box mt={2} mb={2} display="flex" alignItems="center">
        <Typography variant="subtitle1" style={{ flexGrow: 1 }}>
          {grade ? `Subjects — tick the ones used by ${gradeName}` : 'Subjects'}
        </Typography>
        <Button variant="contained" color="primary" onClick={() => setForm(blankForm())}>
          + Create Subject
        </Button>
      </Box>

      {loading ? <CircularProgress /> : (
        <Box>
          {subjects.map((s) => (
            <Paper key={s.uuid} variant="outlined"
              style={{ padding: '8px 12px', marginBottom: 8, display: 'flex', alignItems: 'center' }}>
              <span style={{
                background: chipColor(s.color), color: '#fff', borderRadius: 4,
                padding: '2px 10px', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap'
              }}>
                {s.icon} {s.label}
              </span>
              <Box flexGrow={1} />
              <FormControlLabel
                control={(
                  <Checkbox
                    color="primary"
                    checked={!!linkBySubject[s.uuid]}
                    disabled={!grade}
                    onChange={(e) => toggleUsed(s.uuid, e.target.checked)}
                  />
                )}
                label={grade ? `Used in ${gradeName}` : 'Used'}
              />
              <Button size="small" onClick={() => setForm({
                uuid: s.uuid, label: s.label, icon: s.icon || '🌟',
                color: (s.color || 'slate').toLowerCase()
              })}>Edit</Button>
              <Button size="small" onClick={() => deleteSubject(s)}>Delete</Button>
            </Paper>
          ))}
          {!subjects.length && (
            <Typography variant="body2" color="textSecondary">
              No subjects yet — create one to get started.
            </Typography>
          )}
        </Box>
      )}

      {/* Create / Edit subject dialog */}
      <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth="xs">
        <DialogTitle>{form && form.uuid ? 'Edit Subject' : 'Create Subject'}</DialogTitle>
        <DialogContent>
          {form && (
            <Box display="flex" flexDirection="column" style={{ gap: 12 }} pt={1}>
              {/* Read e.target.value into a local BEFORE setForm: React 16 pools
                  synthetic events, so a functional updater (which runs later, in
                  render) would see a recycled event with target === null. */}
              <TextField label="Subject Name" placeholder="e.g. Sensory Play" value={form.label}
                onChange={(e) => { const label = e.target.value; setForm((f) => ({ ...f, label })) }} />
              <TextField label="Icon (emoji)" value={form.icon}
                onChange={(e) => { const icon = e.target.value; setForm((f) => ({ ...f, icon })) }} />
              <FormControl>
                <InputLabel>Colour</InputLabel>
                <Select value={form.color}
                  onChange={(e) => { const color = e.target.value; setForm((f) => ({ ...f, color })) }}>
                  {COLOR_KEYS.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={saveSubject}>
            {form && form.uuid ? 'Save Changes' : 'Create Subject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Subjects
